import type { Composer, ComposerTranslation } from 'vue-i18n';

/** Vue2 vue-i18n 实例（内部有 _t / _tc） */
type Vue2I18nInstance = {
  locale: string;
  _t: (key: string, ...args: any[]) => string;
  _tc: (key: string, ...args: any[]) => string;
  getLocaleMessage: (locale: string) => LocaleMessageDict;
  mergeLocaleMessage: (locale: string, message: LocaleMessageDict) => void;
};
/** Vue2 vue-i18n 实例或 Vue3 Composer 实例 */
type I18nInstance = Vue2I18nInstance | Composer;

/** 企业组织类型：1 门店 / 2 厂区 / 3 园区 / 4 校区 / 5 仓库 */
type EnterpriseType = '1' | '2' | '3' | '4' | '5';
type NonStoreEnterpriseType = Exclude<EnterpriseType, '1'>;

/** latin 语言的单复数词形 */
type LatinWordForms = {
  singular: string | string[];
  plural: string | string[];
};

/** 简单替换目标（中日泰等） */
type SimpleToMap = Record<NonStoreEnterpriseType, string>;

/** latin 替换目标 */
type LatinToMap = Record<
  NonStoreEnterpriseType,
  {
    singular: string;
    plural: string;
  }
>;

/** 单条术语替换配置 */
type TermReplaceItem =
  | {
      from: string | string[];
      to: SimpleToMap;
    }
  | {
      from: LatinWordForms;
      to: LatinToMap;
    };

/** term-replace.json 数据结构 */
type TermReplaceData = {
  simple: string[];
  'white-list'?: {
    web?: string[];
    h5?: string[];
  };
} & Record<string, string[] | TermReplaceItem[]>;

/** 规范化后的生效规则 */
type SimpleReplaceRule = {
  type: 'simple';
  from: RegExp;
  to: string;
};

type CaseAwareReplaceRule = {
  type: 'caseAware';
  from: RegExp;
  to: string;
};

type LatinReplaceRule = {
  type: 'latin';
  to: { singular: string; plural: string };
  pluralRegex: RegExp | null;
  singularRegex: RegExp | null;
};

type ActiveReplaceRule = SimpleReplaceRule | CaseAwareReplaceRule | LatinReplaceRule;

type LocaleMessageDict = Record<string, unknown>;

const isVue2I18n = (instance: I18nInstance): instance is Vue2I18nInstance =>
  '_t' in instance && typeof (instance as Vue2I18nInstance)._t === 'function';

const getI18nLocale = (instance: I18nInstance): string => {
  const { locale } = instance;
  return typeof locale === 'string' ? locale : String(locale.value);
};

/** vue-i18n对象 */
let i18n: I18nInstance;
/** 已处理过的翻译 key，避免重复检测替换 */
const processedKeys = new Set<string>();
/** 当前企业类型，1: 门店, 2: 厂区, 3: 园区, 4: 校区, 5: 仓库 */
let enterpriseType: EnterpriseType = '1';
/** 当前生效的替换规则 */
let activeReplaceRules: ActiveReplaceRule[] = [];
/** 是否已包装 i18n.t / i18n.tc */
let i18nWrapped = false;
/** 术语替换白名单，不进行替换 */
const termWhiteList = new Set<string>();

/** 将源词的大小写模式应用到目标词 */
const applyCasePattern = (source: string, target: string): string => {
  if (!source || !target) return target;
  if (source === source.toUpperCase()) {
    return target.toUpperCase();
  }
  if (source[0] === source[0].toUpperCase() && source.slice(1) === source.slice(1).toLowerCase()) {
    return target.charAt(0).toUpperCase() + target.slice(1).toLowerCase();
  }
  return target.toLowerCase();
};

/** 转义正则特殊字符 */
const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** 构建字母语言替换正则（词边界匹配） */
const buildLatinWordRegex = (words: string[]): RegExp | null => {
  if (!words?.length) return null;
  const pattern = words.map(escapeRegExp).join('|');
  return new RegExp(`\\b(${pattern})\\b`, 'gi');
};

/** 构建大小写不敏感替换正则 */
const buildCaseAwareRegex = (str: string): RegExp => new RegExp(escapeRegExp(str), 'gi');

/** 将 string | string[] 规范为 string[] */
const toWordList = (value: string | string[] | undefined): string[] => {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value;
  return [];
};

/** 构建 latin 替换规则 */
const buildLatinRule = (
  from: LatinWordForms,
  to: { singular: string; plural: string },
): LatinReplaceRule => ({
  type: 'latin',
  to,
  pluralRegex: buildLatinWordRegex(toWordList(from.plural)),
  singularRegex: buildLatinWordRegex(toWordList(from.singular)),
});

/** 规范化替换规则，返回规则数组 */
const normalizeReplaceRule = (item: TermReplaceItem, isSimple: boolean): ActiveReplaceRule[] => {
  const to = item.to[enterpriseType as NonStoreEnterpriseType];
  if (!to) return [];

  let { from } = item;
  if (!from) return [];

  if (typeof from === 'string') {
    from = [from];
  }

  if (isSimple) {
    return [
      {
        type: 'simple',
        from: new RegExp(
          toWordList(from as string | string[])
            .map(escapeRegExp)
            .join('|'),
          'g',
        ),
        to: to as string,
      },
    ];
  }

  if (Array.isArray(from)) {
    return from.map((word) => ({
      type: 'caseAware' as const,
      from: buildCaseAwareRegex(word),
      to: to as string,
    }));
  }

  if (typeof from === 'object') {
    return [buildLatinRule(from, to as { singular: string; plural: string })];
  }

  return [];
};

/** 对模板文案应用单条替换规则 */
const applyReplaceRule = (template: string, rule: ActiveReplaceRule): string => {
  if (rule.type === 'simple') {
    return template.replace(rule.from, rule.to);
  }
  if (rule.type === 'caseAware') {
    return template.replace(rule.from, (match) => applyCasePattern(match, rule.to));
  }
  if (rule.type === 'latin') {
    let result = template;
    if (rule.pluralRegex) {
      result = result.replace(rule.pluralRegex, (match) => applyCasePattern(match, rule.to.plural));
    }
    if (rule.singularRegex) {
      result = result.replace(rule.singularRegex, (match) =>
        applyCasePattern(match, rule.to.singular),
      );
    }
    return result;
  }
  return template;
};

/** 对模板文案应用术语替换 */
const applyTermReplace = (template: string): string => {
  if (!activeReplaceRules.length) return template;
  return activeReplaceRules.reduce(applyReplaceRule, template);
};

/** 首次翻译时检测替换模板并写回 messages */
const processTermKey = (key: string): void => {
  if (
    enterpriseType === '1' ||
    termWhiteList.has(key) ||
    processedKeys.has(key) ||
    !activeReplaceRules.length
  )
    return;

  const locale = getI18nLocale(i18n);
  const messages = i18n.getLocaleMessage(locale) as LocaleMessageDict;
  const template = messages[key];

  if (typeof template === 'string') {
    const replaced = applyTermReplace(template);
    if (replaced !== template) {
      i18n.mergeLocaleMessage(locale, { [key]: replaced });
    }
  }

  processedKeys.add(key);
};

/** 包装 i18n.t / i18n.tc，懒替换 message 模板 */
export const wrapI18nTranslate = (e: I18nInstance): void => {
  if (i18nWrapped) return;
  i18nWrapped = true;
  i18n = e;

  if (isVue2I18n(i18n)) {
    // vue2 的 i18n 对象
    const _t = i18n._t.bind(i18n);
    const _tc = i18n._tc.bind(i18n);
    i18n._t = function (key: string, ...args: any[]) {
      if (typeof key === 'string') {
        processTermKey(key);
      }
      return _t(key, ...args);
    };
    i18n._tc = function (key: string, ...args: any[]) {
      if (typeof key === 'string') {
        processTermKey(key);
      }
      return _tc(key, ...args);
    };
  } else {
    // vue3 的 i18n 对象（Composer）：tc 已合并到 t
    const originalT = i18n.t.bind(i18n) as ComposerTranslation;
    i18n.t = ((key: unknown, ...args: unknown[]) => {
      if (typeof key === 'string') {
        processTermKey(key);
      }
      return (originalT as (...params: any[]) => string)(key, ...args);
    }) as typeof i18n.t;
  }
};

/** 更新当前生效的替换规则（数据源：term-replace.json） */
export const updateActiveReplaceRules = (data: TermReplaceData, lang: string): void => {
  const isSimple = data.simple.includes(lang);
  data['white-list']?.h5?.forEach((item) => termWhiteList.add(item));
  const rules = (data[lang] as TermReplaceItem[] | undefined) || [];
  activeReplaceRules = rules.flatMap((item) => normalizeReplaceRule(item, isSimple));
};

/** 设置当前企业组织类型 */
export const setEnterpriseType = (type: string): void => {
  processedKeys.clear();
  termWhiteList.clear();
  activeReplaceRules.length && (activeReplaceRules.length = 0);
  enterpriseType = type as EnterpriseType;
};
